import {
  Accordion as MuiAccordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Divider,
  Grid,
  Modal,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import axiosInstance from "../../utils/config/config";

const Accordion = ({ posts, summary, type, heading, description, events }) => {
  const dataList = type === "posts" ? posts : summary;
  const [openModal, setOpenModal] = useState(false);
  const [selectedComments, setSelectedComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, message: "" });

  const fetchComments = async (postId) => {
    setCommentsLoading(true);
    try {
      const response = await axiosInstance.get(
        `/post/comments?postId=${postId}`
      );
      setSelectedComments(response.data || []);
      setOpenModal(true);
    } catch (err) {
      setErrorState({ error: true, message: "Failed to load comments." });
    } finally {
      setCommentsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
        {type === "posts"
          ? "Posts Summary"
          : type === "training"
          ? "Training Summary"
          : "Finance Summary"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
        {description}
      </Typography>

      {type === "posts" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {posts.map((item) => (
            <MuiAccordion
              key={item.id}
              sx={{ borderRadius: 2, overflow: "hidden" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <video
                    src={item.videoURL}
                    style={{
                      width: 80,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                    muted
                  />
                  <Typography sx={{ fontWeight: 600 }}>{item.title}</Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="body2">Likes</Typography>
                    <Typography variant="subtitle1">{item.likes}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="body2">Comments</Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ cursor: "pointer" }}
                      onClick={() => fetchComments(item.id)}
                    >
                      {item.comments.length}
                    </Typography>
                  </Grid>

                  {item.visibility === "payPerView" ||
                  item.visibility === "payPerViewToEveryone" ? (
                    <>
                      <Grid item xs={6} sm={2}>
                        <Typography variant="body2">Purchases</Typography>
                        <Typography variant="subtitle1">
                          {item.purchases}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <Typography variant="body2">Revenue</Typography>
                        <Typography variant="subtitle1">
                          ${item.revenue}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <Typography variant="body2">Price</Typography>
                        <Typography variant="subtitle1">
                          ${item.price}
                        </Typography>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2">Content Type</Typography>
                        <Typography variant="subtitle1">
                          {item.visibility}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </AccordionDetails>
            </MuiAccordion>
          ))}
        </Box>
      )}

      {type === "training" &&
        (() => {
          // Build recurrence count map
          const recurrenceMap = events.reduce((acc, ev) => {
            if (ev.parentEventId != null) {
              acc[ev.parentEventId] = (acc[ev.parentEventId] || 0) + 1;
            }
            return acc;
          }, {});

          // Only show parent trainings (non-recurring events)
          const parentTrainings = events.filter(
            (ev) => ev.parentEventId == null
          );

          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {parentTrainings.map((item) => {
                const isVideo = item.mediaURL?.match(/\.(mp4|webm|ogg|mov)$/i);
                const recurrenceCount = recurrenceMap[item.id] || 0;

                return (
                  <MuiAccordion
                    key={item.id}
                    sx={{ borderRadius: 2, overflow: "hidden" }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        {isVideo ? (
                          <video
                            src={item.mediaURL}
                            style={{
                              width: 80,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                            muted
                          />
                        ) : (
                          <img
                            src={item.mediaURL}
                            alt={item.name}
                            style={{
                              width: 80,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                        )}
                        <Typography sx={{ fontWeight: 600 }}>
                          {item.name}
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2">Start Date</Typography>
                          <Typography variant="subtitle1">
                            {item.startDateTime
                              ? new Date(item.startDateTime).toLocaleString()
                              : "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2">Price</Typography>
                          <Typography variant="subtitle1">
                            {item.price ? `$${item.price}` : "Free"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2">Categories</Typography>
                          <Typography variant="subtitle1">
                            {item.category?.join(", ") || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2">
                            Recurrence Type
                          </Typography>
                          <Typography variant="subtitle1">
                            {item.recurrenceType || "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2">
                            Recurrence End
                          </Typography>
                          <Typography variant="subtitle1">
                            {item.recurrenceEndDate
                              ? new Date(
                                  item.recurrenceEndDate
                                ).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2">Recurrences</Typography>
                          <Typography variant="subtitle1">
                            {recurrenceCount}
                          </Typography>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </MuiAccordion>
                );
              })}
            </Box>
          );
        })()}

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            borderRadius: 2,
            p: 3,
            maxHeight: "80vh",
            overflowY: "auto",
            width: 400,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Comments
          </Typography>
          {commentsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : selectedComments.length > 0 ? (
            selectedComments.map((comment, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography fontWeight={600}>
                  {comment.User?.firstName} {comment.User?.lastName}
                </Typography>
                <Typography variant="caption">
                  {new Date(comment.createdAt).toLocaleString()}
                </Typography>
                <Typography>{comment.comment}</Typography>
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))
          ) : (
            <Typography>No comments yet.</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Accordion;
